import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Loader2, Star } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import Conviction from '../components/lens/Conviction'
import LensBand from '../components/lens/LensBand'
import VerdictLegend from '../components/lens/VerdictLegend'
import FairPotenzial from '../components/lens/FairPotenzial'
import LoginGate from '../components/common/LoginGate'
import ProGate from '../components/common/ProGate'
import UnscharfeIdeen from '../components/common/UnscharfeIdeen'
import { Fehler, Laden, Leer } from '../components/common/States'
import { useIdeasFilter, useIdeasOptions, useWatchlist, useWatchlistToggle } from '../hooks/useQualyz'
import { ApiError, fehlerText } from '../lib/api'
import {
  ALLE_LINSEN,
  LENSES,
  POSITION_OBERGRENZE_PCT,
  formatDezimal,
  formatZeitpunkt,
  positionFuer,
  verlustBasisText,
  type LensKey,
} from '../lib/lenses'
import { grenzenVon } from '../lib/tarife'
import type { FilterRequest, Idea } from '../services/qualyz'

const LIMIT = 100

const STANDARD_BUDGETS = [
  { name: 'konservativ', pct: 1, standard: false },
  { name: 'ausgewogen', pct: 2, standard: true },
  { name: 'offensiv', pct: 5, standard: false },
]

interface Auswahl {
  linsen: LensKey[]
  gates: string[]
}

const STANDARD_AUSWAHL: Auswahl = { linsen: [...ALLE_LINSEN], gates: [] }

function gleich(a: Auswahl, b: Auswahl): boolean {
  const s = (x: string[]) => [...x].sort().join(',')
  return s(a.linsen) === s(b.linsen) && s(a.gates) === s(b.gates)
}

/**
 * Die Standardansicht schickt genau {lenses: null, gates: null, sizing: true,
 * limit: 100} -- das trifft den vorgewaermten Eintrag im Backend-Cache.
 */
function anfrageFuer(a: Auswahl): FilterRequest {
  return {
    lenses: a.linsen.length === ALLE_LINSEN.length ? null : a.linsen,
    gates: a.gates.length > 0 ? a.gates : null,
    sizing: true,
    limit: LIMIT,
  }
}

/** Sortierbare Spalten. "rang" ist die Rangfolge des Backends: Konviktion, urteilende Linsen, Score. */
type Spalte = 'rang' | 'titel' | 'konviktion' | 'position' | 'fair' | 'score'

interface Sortierung {
  spalte: Spalte
  absteigend: boolean
}

const STANDARD_SORTIERUNG: Sortierung = { spalte: 'rang', absteigend: false }

/** Erster Klick auf eine Spalte: Titel von A nach Z, Zahlen vom groessten Wert her. */
const ERST_ABSTEIGEND: Record<Spalte, boolean> = {
  rang: false,
  titel: false,
  konviktion: true,
  position: true,
  // Hoechstes Potenzial zuerst: Wer nach dem fairen Wert sortiert, sucht die
  // Titel darunter.
  fair: true,
  score: true,
}

/**
 * Mittleres Potenzial bis zum fairen Wert nach Ø-KGV und Ø-KUV -- nur Basen in der
 * ueblichen Spanne. Die Dividende zaehlt fuer die Sortierung nicht mit, weil
 * sie nur ein Drittel der Titel hat und die Reihenfolge sonst davon abhinge,
 * ob einer ausschuettet.
 */
function fairMittel(idea: Idea): number | null {
  const werte = (['kgv', 'kuv'] as const)
    .map((b) => idea.fairwert?.[b])
    .filter((e): e is { potenzial_pct: number; ausserhalb: boolean } => Boolean(e) && !e!.ausserhalb)
    .map((e) => e.potenzial_pct)
  return werte.length ? werte.reduce((a, b) => a + b, 0) / werte.length : null
}

/**
 * Sortiert im Browser (Wunsch Kim, 14.09.2026) -- die Liste ist geladen, eine
 * neue Rechnung im Backend braucht es dafuer nicht. Gleichstand folgt der
 * Rangfolge des Backends; fehlende Werte (keine Positionsgroesse) stehen
 * immer am Ende, egal in welche Richtung.
 */
function sortieren(
  ideen: Idea[],
  sortierung: Sortierung,
  rang: Map<string, number>,
  positionVon: (idea: Idea) => number | null,
): Idea[] {
  if (sortierung.spalte === 'rang') return ideen
  const wert = (idea: Idea): number | string | null => {
    switch (sortierung.spalte) {
      case 'titel':
        return idea.symbol
      case 'konviktion':
        return idea.conviction
      case 'position':
        return positionVon(idea)
      case 'fair':
        return fairMittel(idea)
      default:
        return idea.avg_score
    }
  }
  const nachRang = (a: Idea, b: Idea) => (rang.get(a.symbol) ?? 0) - (rang.get(b.symbol) ?? 0)
  const richtung = sortierung.absteigend ? -1 : 1
  return [...ideen].sort((a, b) => {
    const wa = wert(a)
    const wb = wert(b)
    if (wa === null || wb === null) return wa === wb ? nachRang(a, b) : wa === null ? 1 : -1
    const vergleich =
      typeof wa === 'string' && typeof wb === 'string' ? wa.localeCompare(wb, 'de') : Number(wa) - Number(wb)
    return vergleich !== 0 ? vergleich * richtung : nachRang(a, b)
  })
}

function SortKopf({
  spalte,
  label,
  hinweis,
  sortierung,
  onSortieren,
  className,
}: {
  spalte: Spalte
  label: string
  hinweis: string
  sortierung: Sortierung
  onSortieren: (spalte: Spalte) => void
  className?: string
}) {
  const gewaehlt = sortierung.spalte === spalte
  // Die Rangfolge ist der Normalzustand -- ihr Kopf zeigt keinen Pfeil.
  const aktiv = gewaehlt && spalte !== 'rang'
  const Pfeil = aktiv ? (sortierung.absteigend ? ArrowDown : ArrowUp) : ArrowUpDown
  return (
    <th
      scope="col"
      className={className}
      aria-sort={gewaehlt ? (sortierung.absteigend ? 'descending' : 'ascending') : 'none'}
    >
      <button
        type="button"
        className={`q-sort${aktiv ? ' is-active' : ''}`}
        onClick={() => onSortieren(spalte)}
        title={hinweis}
      >
        {label}
        {spalte !== 'rang' && <Pfeil aria-hidden="true" />}
      </button>
    </th>
  )
}

export default function LensScreenerPage() {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return null
  if (!isAuthenticated) {
    return (
      <div className="q-page">
        <section className="q-card">
          <LoginGate titel="Der Screener ist für angemeldete Nutzer." zeilen={7} />
        </section>
      </div>
    )
  }
  return <Screener />
}

function Screener() {
  const { user } = useAuth()
  // Bezahlschranke (tarife.py): Kostenlose Konten sehen die Standardansicht,
  // gekuerzt auf zehn Titel. Eigene Linsen und Ausschlusskriterien sind Pro --
  // jede neue Kombination kostet das Backend Rechenzeit.
  const eigeneFilter = grenzenVon(user).eigene_filter

  // Linsen und Gates wirken erst auf "Anwenden". Konviktion, Risikobudget und
  // Sortierung dagegen rechnet der Browser -- die aendern sich sofort.
  const [angewandt, setAngewandt] = useState<Auswahl>(STANDARD_AUSWAHL)
  const [entwurf, setEntwurf] = useState<Auswahl>(STANDARD_AUSWAHL)
  const [minKonviktion, setMinKonviktion] = useState(4)
  const [budget, setBudget] = useState(2)
  const [sortierung, setSortierung] = useState<Sortierung>(STANDARD_SORTIERUNG)

  const optionen = useIdeasOptions()
  const anfrage = useMemo(() => anfrageFuer(angewandt), [angewandt])
  const ideen = useIdeasFilter(anfrage)
  const watchlist = useWatchlist()
  const merken = useWatchlistToggle()

  const budgets = optionen.data?.risikobudgets?.length ? optionen.data.risikobudgets : STANDARD_BUDGETS
  const obergrenze = optionen.data?.position_obergrenze_pct ?? POSITION_OBERGRENZE_PCT
  const gates = optionen.data?.gates ?? []
  const gemerkt = new Set((watchlist.data?.items ?? []).map((i) => i.symbol))
  const geaendert = !gleich(entwurf, angewandt)
  const maxKonviktion = angewandt.linsen.length
  const schwelle = Math.min(minKonviktion, maxKonviktion)
  const gewaehlt = angewandt.linsen.length === ALLE_LINSEN.length ? null : angewandt.linsen

  const alle = ideen.data?.ideas ?? []
  const treffer = alle.filter((i) => i.conviction >= schwelle)
  const gesamt = ideen.data?.gesamt ?? alle.length
  const gekuerzt = (ideen.data?.begrenzt_auf ?? null) !== null && gesamt > alle.length

  const rang = new Map(treffer.map((idea, index) => [idea.symbol, index + 1] as [string, number]))
  const positionVon = (idea: Idea) => positionFuer(idea.sizing?.verlustannahme_pct, budget, obergrenze).pct
  const sortiert = sortieren(treffer, sortierung, rang, positionVon)
  const zeitueberschreitung = ideen.error instanceof ApiError && ideen.error.status === 504

  const sortierenNach = (spalte: Spalte) =>
    setSortierung((s) =>
      spalte === 'rang'
        ? STANDARD_SORTIERUNG
        : s.spalte === spalte
          ? { spalte, absteigend: !s.absteigend }
          : { spalte, absteigend: ERST_ABSTEIGEND[spalte] },
    )

  const linseUmschalten = (key: LensKey) =>
    setEntwurf((e) => {
      const an = e.linsen.includes(key)
      if (an && e.linsen.length === 1) return e // eine Linse muss bleiben
      const neu = an ? e.linsen.filter((k) => k !== key) : [...e.linsen, key]
      return { ...e, linsen: ALLE_LINSEN.filter((k) => neu.includes(k)) }
    })

  const gateUmschalten = (name: string) =>
    setEntwurf((e) => ({
      ...e,
      gates: e.gates.includes(name) ? e.gates.filter((g) => g !== name) : [...e.gates, name],
    }))

  const anwenden = () => {
    setAngewandt(entwurf)
    setMinKonviktion((m) => Math.min(m, entwurf.linsen.length))
  }

  const proHinweis = eigeneFilter ? null : (
    <span className="q-pro-badge" title="Eigene Auswahl mit Pro">
      Pro
    </span>
  )

  return (
    <div className="q-page">
      <section className="q-controls" aria-label="Filter">
        <div className="q-cgroup q-cgroup--wide">
          <span className="q-clabel" id="linsen-label">
            Linsen in der Konviktion
            {proHinweis}
          </span>
          <div className="q-togglechips" role="group" aria-labelledby="linsen-label">
            {LENSES.map((l) => (
              <button
                key={l.key}
                type="button"
                className="q-toggle"
                aria-pressed={entwurf.linsen.includes(l.key)}
                onClick={() => linseUmschalten(l.key)}
                disabled={!eigeneFilter}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {gates.length > 0 && (
          <div className="q-cgroup">
            <span className="q-clabel" id="gates-label">
              Ausschlusskriterien
              {proHinweis}
            </span>
            <div className="q-togglechips" role="group" aria-labelledby="gates-label">
              {gates.map((g) => (
                <button
                  key={g.name}
                  type="button"
                  className="q-toggle"
                  aria-pressed={entwurf.gates.includes(g.name)}
                  onClick={() => gateUmschalten(g.name)}
                  disabled={!eigeneFilter}
                >
                  {g.beschreibung}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="q-cgroup">
          <label className="q-clabel" htmlFor="min-konviktion">
            Min. Konviktion
          </label>
          <div className="q-range-wrap">
            <input
              id="min-konviktion"
              type="range"
              min={0}
              max={maxKonviktion}
              step={1}
              value={schwelle}
              onChange={(e) => setMinKonviktion(Number(e.target.value))}
            />
            <span className="q-range-val">{schwelle}</span>
          </div>
        </div>

        <div className="q-cgroup">
          <label className="q-clabel" htmlFor="budget">
            Risikobudget
          </label>
          <select id="budget" className="q-select" value={budget} onChange={(e) => setBudget(Number(e.target.value))}>
            {budgets.map((b) => (
              <option key={b.name} value={b.pct}>
                {b.name} · {formatDezimal(b.pct, 0)} %
              </option>
            ))}
          </select>
        </div>

        {eigeneFilter && geaendert && (
          <div className="q-controls-actions">
            <button type="button" className="q-btn q-btn-ghost q-btn-sm" onClick={() => setEntwurf(angewandt)}>
              Verwerfen
            </button>
            <button type="button" className="q-btn q-btn-primary q-btn-sm" onClick={anwenden}>
              Anwenden
            </button>
          </div>
        )}
      </section>

      <VerdictLegend />

      {/* Scheitert nur das Auffrischen einer schon geladenen Liste, bleibt sie
          stehen. Eine neue Auswahl dagegen hat noch keine Daten -- s. unten. */}
      {ideen.isError && ideen.data && (
        <div className="q-alert q-alert--warn" role="alert">
          <AlertTriangle aria-hidden="true" />
          <span>
            {zeitueberschreitung
              ? 'Das Auffrischen braucht länger als erwartet. Angezeigt wird der vorige Stand.'
              : fehlerText(ideen.error)}
          </span>
          <button
            type="button"
            className="q-btn q-btn-ghost q-btn-sm"
            style={{ marginLeft: 'auto' }}
            onClick={() => ideen.refetch()}
          >
            Erneut laden
          </button>
        </div>
      )}

      <section className="q-card q-card--ladeflaeche" aria-busy={ideen.isFetching}>
        {ideen.isPending ? (
          <Laden
            text="Die Linsen bewerten das Universum …"
            hinweis="Beim ersten Abruf dieser Auswahl dauert das etwas. Danach ist sie zwischengespeichert."
          />
        ) : ideen.isError && !ideen.data ? (
          // Nach einem 504 rechnet das Backend weiter und legt das Ergebnis
          // ab -- ein zweiter Versuch kurz danach kommt aus dem Speicher.
          <Fehler
            fehler={
              zeitueberschreitung
                ? new Error('Diese Auswahl braucht länger als erwartet. Sie wird im Hintergrund fertig gerechnet, bitte gleich erneut versuchen.')
                : ideen.error
            }
            nochmal={() => ideen.refetch()}
          />
        ) : (
          <>
            {/* Ladekreis ueber der ganzen Kachel (Wunsch Kim, 14.09.2026): Der
                schmale Hinweis oben ging unter, und die Rechnung dauert Sekunden. */}
            {ideen.isFetching && (
              <div className="q-ladeschicht" role="status" aria-live="polite">
                <div className="q-ladeschicht-box">
                  <Loader2 className="q-spin" aria-hidden="true" />
                  <span>Neue Auswahl wird berechnet …</span>
                  <span className="q-state-hint">Das kann bis zu 20 Sekunden dauern.</span>
                </div>
              </div>
            )}
            <div className="q-card-head">
              <p className="q-meta">
                <b>{treffer.length}</b> Titel mit Konviktion ≥ {schwelle} ·{' '}
                {gekuerzt ? `die ersten ${alle.length} von ${gesamt}` : `${alle.length} bewertet`}
                {ideen.data?.berechnet_am ? ` · Stand ${formatZeitpunkt(ideen.data.berechnet_am)}` : ''}
              </p>
            </div>

            {treffer.length === 0 ? (
              <Leer
                text={
                  eigeneFilter
                    ? 'Kein Titel erreicht diese Konviktion. Schwelle senken oder mehr Linsen einbeziehen.'
                    : 'Kein Titel erreicht diese Konviktion. Schwelle senken.'
                }
              />
            ) : (
              <div className={`q-table-wrap${ideen.isFetching ? ' is-stale' : ''}`}>
                {/* Reihenfolge nach Wichtigkeit: Was man sieht, bevor man
                    seitlich scrollt, soll Titel, Konviktion, Form und
                    Position sein -- der Durchschnitt ist Beiwerk. */}
                <table className="q-table">
                  <thead>
                    <tr>
                      {/* Hinweise enthalten die sichtbare Beschriftung -- Sprachsteuerung
                          ("klicke Titel") findet den Knopf sonst nicht. */}
                      <SortKopf
                        spalte="rang"
                        label="#"
                        hinweis="# Rangfolge: Konviktion, dann Zahl der urteilenden Linsen, dann Score"
                        className="q-rank"
                        sortierung={sortierung}
                        onSortieren={sortierenNach}
                      />
                      <SortKopf
                        spalte="titel"
                        label="Titel"
                        hinweis="Titel nach Kürzel sortieren"
                        sortierung={sortierung}
                        onSortieren={sortierenNach}
                      />
                      <SortKopf
                        spalte="konviktion"
                        label="Konviktion"
                        hinweis="Nach Konviktion sortieren"
                        sortierung={sortierung}
                        onSortieren={sortierenNach}
                      />
                      <th scope="col" className="q-center">
                        Q · M · E · C · L · I · S
                      </th>
                      <SortKopf
                        spalte="position"
                        label="max. Position"
                        hinweis="Nach max. Position sortieren"
                        className="q-num"
                        sortierung={sortierung}
                        onSortieren={sortierenNach}
                      />
                      <SortKopf
                        spalte="fair"
                        label="Potenzial"
                        hinweis="Potenzial bis zum fairen Wert nach Ø-KGV und Ø-KUV, sortiert nach dem Mittel"
                        className="q-num"
                        sortierung={sortierung}
                        onSortieren={sortierenNach}
                      />
                      <SortKopf
                        spalte="score"
                        label="Ø Score"
                        hinweis="Nach Ø Score sortieren"
                        className="q-num"
                        sortierung={sortierung}
                        onSortieren={sortierenNach}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {sortiert.map((idea) => {
                      const position = positionFuer(idea.sizing?.verlustannahme_pct, budget, obergrenze)
                      const hinweise = idea.sizing?.hinweise ?? []
                      const istGemerkt = gemerkt.has(idea.symbol)
                      const laeuft = merken.isPending && merken.variables?.symbol === idea.symbol
                      const drawdown = idea.sizing?.max_drawdown_pct
                      return (
                        <tr key={idea.symbol}>
                          <td className="q-rank">{rang.get(idea.symbol)}</td>
                          <td>
                            <div className="q-titlewrap">
                              <button
                                type="button"
                                className={`q-star${istGemerkt ? ' is-on' : ''}`}
                                onClick={() => merken.mutate({ symbol: idea.symbol, merken: !istGemerkt })}
                                disabled={laeuft}
                                aria-pressed={istGemerkt}
                                aria-label={
                                  istGemerkt ? `${idea.symbol} aus der Watchlist entfernen` : `${idea.symbol} merken`
                                }
                              >
                                <Star fill={istGemerkt ? 'currentColor' : 'none'} aria-hidden="true" />
                              </button>
                              <Link
                                className="q-titlelink"
                                to={`/aktie/${encodeURIComponent(idea.symbol)}`}
                                title={`${idea.symbol} im Detail ansehen`}
                              >
                                <div className="q-titlecell">
                                  <span className="q-sym">{idea.symbol}</span>
                                  <span className="q-name" title={idea.name}>
                                    {idea.name}
                                  </span>
                                  {idea.sector && <span className="q-sector">{idea.sector}</span>}
                                </div>
                              </Link>
                            </div>
                          </td>
                          <td>
                            <Conviction wert={idea.conviction} von={idea.lenses_total} pips />
                          </td>
                          <td>
                            <LensBand idea={idea} gewaehlt={gewaehlt} />
                          </td>
                          <td className="q-num">
                            {position.pct === null ? (
                              <span className="q-pos-basis" title="Zu wenig Kurshistorie für eine Positionsgröße">
                                –
                              </span>
                            ) : (
                              <div className="q-pos">
                                <span className="q-pos-val">
                                  {formatDezimal(position.pct, 2)} %
                                  {hinweise.length > 0 && (
                                    <span className="q-hint" title={hinweise.join(' · ')}>
                                      <AlertTriangle aria-label={`Hinweis: ${hinweise.join(' · ')}`} />
                                    </span>
                                  )}
                                </span>
                                <span className="q-pos-basis">
                                  {position.gedeckelt ? 'Deckel · ' : ''}
                                  {verlustBasisText(idea.sizing?.verlustannahme_quelle)}
                                  {typeof drawdown === 'number' ? ` · DD ${formatDezimal(drawdown, 0)} %` : ''}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="q-num">
                            <div className="q-fairstack">
                              {(['kgv', 'kuv'] as const).map((b) => (
                                <span key={b} className="q-fairstack-zeile">
                                  <span className="q-fairstack-basis">{b.toUpperCase()}</span>
                                  <FairPotenzial
                                    potenzial={idea.fairwert?.[b]?.potenzial_pct}
                                    ausserhalb={idea.fairwert?.[b]?.ausserhalb}
                                    titel={`Ø-${b.toUpperCase()}`}
                                  />
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="q-num">
                            <div className="q-score">{formatDezimal(idea.avg_score, 1)}</div>
                            <div className="q-scorebar" aria-hidden="true">
                              <div
                                className="q-scorebar-fill"
                                style={{ width: `${Math.max(3, Math.min(100, idea.avg_score))}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {gekuerzt && <ProGate titel={`Alle ${gesamt} Titel mit Pro`} vorschau={<UnscharfeIdeen anzahl={4} />} />}
          </>
        )}
      </section>

      {merken.isError && (
        <div className="q-alert q-alert--warn" role="alert">
          <AlertTriangle aria-hidden="true" />
          {fehlerText(merken.error)}
        </div>
      )}
    </div>
  )
}
